import React from 'react';
import { Box, Paper, Skeleton, Stack } from '@mui/material';

export interface LoadingSkeletonProps {
  /** Layout preset to mimic the real content shape. */
  variant?: 'list' | 'cards' | 'table' | 'form' | 'detail' | 'stats';
  /** Number of repeated items/rows. Default 5. */
  count?: number;
  /** Columns for the 'cards'/'stats' grid. Default auto-fill. */
  minItemWidth?: number;
}

/**
 * Content-shaped loading placeholders. Prefer this over a bare spinner so the
 * page keeps its layout while data loads (less layout shift, calmer UX).
 */
const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  variant = 'list',
  count = 5,
  minItemWidth = 220,
}) => {
  if (variant === 'stats') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${minItemWidth}px, 1fr))`,
          gap: 2,
        }}
      >
        {Array.from({ length: count }).map((_, i) => (
          <Paper key={i} sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Skeleton variant="rounded" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="55%" height={18} />
                <Skeleton variant="text" width="75%" height={30} />
              </Box>
            </Stack>
          </Paper>
        ))}
      </Box>
    );
  }

  if (variant === 'cards') {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${minItemWidth}px, 1fr))`,
          gap: 2,
        }}
      >
        {Array.from({ length: count }).map((_, i) => (
          <Paper key={i} sx={{ p: 2, borderRadius: 2 }}>
            <Skeleton variant="rounded" width="100%" height={120} sx={{ mb: 1.5 }} />
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="text" width="45%" />
          </Paper>
        ))}
      </Box>
    );
  }

  if (variant === 'table') {
    return (
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Skeleton variant="rounded" height={40} sx={{ mb: 1 }} />
        {Array.from({ length: count }).map((_, i) => (
          <Skeleton key={i} variant="text" height={36} />
        ))}
      </Paper>
    );
  }

  if (variant === 'form') {
    return (
      <Stack spacing={2.5}>
        {Array.from({ length: count }).map((_, i) => (
          <Box key={i}>
            <Skeleton variant="text" width={120} height={18} sx={{ mb: 0.5 }} />
            <Skeleton variant="rounded" height={48} />
          </Box>
        ))}
      </Stack>
    );
  }

  if (variant === 'detail') {
    return (
      <Stack spacing={2}>
        <Skeleton variant="text" width="40%" height={32} />
        {Array.from({ length: count }).map((_, i) => (
          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Skeleton variant="text" width="30%" />
            <Skeleton variant="text" width="50%" />
          </Box>
        ))}
      </Stack>
    );
  }

  // 'list' (default) — stacked card rows, mirrors the mobile data-card layout.
  return (
    <Stack spacing={2}>
      {Array.from({ length: count }).map((_, i) => (
        <Paper key={i} sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.25}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={20} />
            <Skeleton variant="text" width="80%" height={20} />
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Skeleton variant="rounded" width={64} height={28} />
              <Skeleton variant="rounded" width={64} height={28} />
            </Box>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
};

export default LoadingSkeleton;
